<template>
  <v-autocomplete
    color="black"
    item-color="primary"
    v-model="chosenTractType"
    :search-input.sync="search"
    :class="{ 'collapse-selection-input': collapseSelectionInput && chosenTractType && !search }"
    data-testid="tract-type"
    :label="$t('tractType')"
    :items="filteredTractTypes"
    return-object
    item-text="name"
    item-value="name"
    :rules="(readonly || !required) ? [] : [rules.required]"
    :persistent-hint="readonly ? false : persistentHint"
    :hint="readonly ? '' : hint"
    :readonly="readonly"
    :append-icon="readonly ? '' : '$dropdown'"
    @click="readonly ? $event.stopPropagation() : null"
    @mousedown="readonly ? $event.preventDefault() : null"
    :item-disabled="disableItem"
    >
      <template #item="{item}">
        <span>
          <Icon :icon="getCategoryIcon(item)" :iconColor="getIconColor(item)" :tooltipText="getCategoryTooltip(item)" :small="false" style="pointer-events: auto;"/>
          <Icon
          v-if="TractTypeSpecialization.forInt(item.specialization).icon"
          :icon="TractTypeSpecialization.forInt(item.specialization).icon"
          :tooltipText="TractTypeSpecialization.forInt(item.specialization).name"
          :small="false"
          :iconColor="getIconColor(item)"
          style="pointer-events: auto;"/>
          {{item.name}}
        </span>
      </template>
      <template #selection="{item}">
        <span>
          <Icon :icon="getCategoryIcon(item)" :iconColor="getIconColor(item)" :tooltipText="getCategoryTooltip(item)" :small="false" style="pointer-events: auto;"/>
          <Icon
          v-if="TractTypeSpecialization.forInt(item.specialization).icon"
          :icon="TractTypeSpecialization.forInt(item.specialization).icon"
          :tooltipText="TractTypeSpecialization.forInt(item.specialization).name"
          :small="false"
          :iconColor="getIconColor(item)"
          style="pointer-events: auto;"/>
          {{item.name}}
        </span>
      </template>
    </v-autocomplete>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { TractTypeCategory, TractTypeSpecialization } from '@/utils/Enumerations.js'
import { rules } from '@/utils/FormRules'
import { theme, helpers } from '@/plugins/theme.js'

export default {
  name: 'TractTypeSelector',

  components: {
    Icon: () => import('@/components/helper/Icon.vue')
  },

  props: {
    tractTypeId: {
      type: Number,
      default: undefined
    },
    hint: {
      type: String,
      default: '',
      required: false
    },
    persistentHint: {
      type: Boolean,
      default: false,
      required: false
    },
    propFetchTractTypes: {
      type: Boolean,
      default: true,
      required: false
    },
    filter: {
      type: Function,
      default: null,
      required: false
    },
    selectFirst: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    required: {
      type: Boolean,
      default: true
    },
    disableItem: {
      type: Function,
      default: () => false
    },
    collapseSelectionInput: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    TractTypeSpecialization,
    TractTypeCategory,
    chosenTractType: undefined,
    search: null,
    rules
  }),

  created () {
    this.refreshTractTypes(this.tractTypeId, true)
  },

  watch: {
    chosenTractType (val) {
      this.$emit('tract-type', val)
    },

    tractTypeId (val) {
      if (!val) return
      this.refreshTractTypes(val)
    }
  },

  computed: {
    ...mapGetters('tract-type', ['allTractTypes']),
    filteredTractTypes () {
      return this.filter
        ? this.allTractTypes.filter(this.filter)
        : this.allTractTypes
    }
  },

  methods: {
    ...mapActions('tract-type', ['fetchTractTypes', 'createTractType']),
    getCategoryIcon: ({ category }) => (TractTypeCategory.forInt(category) ?? TractTypeCategory.Delivered).icon,
    getCategoryTooltip: ({ category }) => TractTypeCategory.fromInt(category),
    getIconColor (item) {
      if (this.disableItem(item) && !this.readonly) return 'grey'
      return TractTypeCategory.Stumpage.value === item.category ? helpers.getShade(10, theme.info) : helpers.getTint(35, theme.info)
    },

    async refreshTractTypes (tractTypeId, creation = false) {
      if (!creation || this.propFetchTractTypes) await this.fetchTractTypes()
      if (tractTypeId) {
        this.chosenTractType = this.allTractTypes.find(tt => tt.tractTypeId === tractTypeId)
        return
      }
      if (this.selectFirst && this.filteredTractTypes.length === 1) {
        this.chosenTractType = this.filteredTractTypes[0]
      }
    }
  }
}
</script>

<style scoped>
/* Collapse the empty search input so a selected value uses the full width even when
   focused (Vuetify forces a 64px min-width otherwise); it returns once the user types. */
.collapse-selection-input ::v-deep .v-select__selections input {
  width: 0 !important;
  min-width: 0 !important;
  flex: 0 0 0 !important;
}
</style>
